import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Generic, Icon } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import GenericTable from "../../../GenericTable";
import { useModal } from "../../../../context";
import { Loader, Confirmation } from "../../..";

import {
  generateCertificationColumns,
  generateLicenseColumns,
  generateSkillCoursesColumns,
} from "../../columns";
import QualificationCodesModal from "../QualificationCodesModal";
import { DELETE_SYSTEM_CODE_MUTATION } from "../../../../graphql";

const QualificationsTable = ({ qualification, data, loading, refetch, isReadOnly }) => {
  const { setModalOpen } = useModal();
  const [deleteSystemCode] = useMutation(DELETE_SYSTEM_CODE_MUTATION);

  const COLUMNS = useMemo(() => {
    switch (qualification) {
      case "certifications":
        return generateCertificationColumns();
      case "licenses":
        return generateLicenseColumns();
      default:
        return generateSkillCoursesColumns();
    }
  }, [qualification]);

  const handleDelete = useCallback(
    (codeId) => {
      const performDelete = async () => {
        try {
          await deleteSystemCode({
            variables: {
              where: { id: codeId },
            },
          });
          toast.success("Qualification code removed successfully.");
          if (typeof refetch === "function") {
            await refetch();
          }
        } catch (err) {
          toast.error(
            "There was an error deleting the qualification code. Please contact support."
          );
        } finally {
          setModalOpen(false, "");
        }
      };

      setModalOpen(
        true,
        <Confirmation
          message="Are you sure you want to delete this Qualification Code?"
          onCancel={() => setModalOpen(false, "")}
          onConfirm={performDelete}
        />
      );
    },
    [deleteSystemCode, refetch, setModalOpen]
  );

  const DeleteCell = ({ cell }) => (
    <Generic
      style={{ textAlign: "center", cursor: "pointer" }}
      title="Delete"
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        handleDelete(cell?.row?.values?.id);
      }}
    >
      <Icon color="danger">
        <FontAwesomeIcon icon="times" />
      </Icon>
    </Generic>
  );
  DeleteCell.propTypes = {
    cell: PropTypes.object.isRequired,
  };

  const columnsWithDelete = useMemo(() => {
    if (isReadOnly) return COLUMNS;
    const deleteColumn = {
      Header: "",
      accessor: "id",
      Cell: DeleteCell,
    };
    return [deleteColumn, ...COLUMNS];
  }, [COLUMNS, handleDelete, isReadOnly]);

  const handleRowClick = ({ id }) => {
    if (isReadOnly) return;
    setModalOpen(
      true,
      <QualificationCodesModal
        id={id}
        qualification={qualification}
        onComplete={() => setModalOpen(false, "")}
      />
    );
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          width: "100%",
        }}
      >
        <Loader />;
      </div>
    );
  }

  return (
    <div className="generic-table-wrapper">
      <GenericTable
        isHoverable
        columns={columnsWithDelete}
        data={data}
        onRowClick={handleRowClick}
    />
    </div>
  );
};

QualificationsTable.propTypes = {
  qualification: PropTypes.string.isRequired,
  data: PropTypes.array,
  loading: PropTypes.bool,
  refetch: PropTypes.func,
  isReadOnly: PropTypes.bool,
};
QualificationsTable.defaultProps = {
  data: [],
  loading: false,
  refetch: () => null,
  isReadOnly: false,
};

export default QualificationsTable;
